import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { finalize } from "rxjs/operators";
import { NotifierService } from "angular-notifier";
import { StockHoldReleaseService } from "src/app/services/autopac/stock-hold-release.service";
import { FormField } from "src/app/interfaces/form-field.model";
import {
  faPencil,
  faPrint,
  faEraser,
  faSquare,
  faSquareCheck,
} from "@fortawesome/free-solid-svg-icons";

@Component({
  selector: "app-stock-hold-release",
  templateUrl: "./stock-hold-release.component.html",
  styleUrls: ["./stock-hold-release.component.css"],
})
export class StockHoldReleaseComponent implements OnInit, OnDestroy {
  faPencil = faPencil;
  faPrint = faPrint;
  faEraser = faEraser;
  faSquare = faSquare;
  faSquareCheck = faSquareCheck;
  selectedTab = "single";
  searchFormFields: FormField[] = [];
  batchFormFields: FormField[] = [];
  vehicleList: any[] = [];
  loadedVin = "";
  registered = false;
  singleBusy = false;
  batchBusy = false;
  batchMessage = "";
  private subscriptions = new Subscription();
  private vehicleRequest?: Subscription;
  private colourRequest?: Subscription;
  private listRequest?: Subscription;
  tableHeaders_vehicleList = [
    { title: "VIN NUMBER", id: "vinNumber", type: "data" },
    { title: "BUYER'S NAME", id: "buyerName", type: "data" },
    { title: "ETA DATE", id: "etaDate", type: "data" },
    { title: "HOLD DATE", id: "holdDate", type: "data" },
    { title: "STOCK STATUS", id: "stockStatus", type: "data" },
    { title: "SELECT", id: "selected", type: "checkbox" },
  ];
  constructor(
    private stockHoldService: StockHoldReleaseService,
    private notifier: NotifierService,
  ) {}

  ngOnInit(): void {
    this.initializeFormFields();
    this.batchFormFields = [
      {
        type: "select",
        id: "model",
        label: "Model",
        options: [],
        value: "",
      },
      {
        type: "select",
        id: "stockStatus",
        label: "Stock Status",
        value: "H",
        options: this.searchFormFields.find(
          (f) => f.id === "stockStatus",
        )!.options,
      },
      {
        type: "select",
        id: "colour",
        label: "Colour",
        options: [],
        value: "",
      },
    ];
    this.subscriptions.add(
      this.stockHoldService.getAll().subscribe({
        next: (response) => {
          if (!Array.isArray(response.body))
            return this.error("Unable to load sales consultants.");
          this.searchFormFields.find(
            (f) => f.id === "salesConsultant",
          )!.options = response.body
            .filter(
              (user: any) =>
                user.RoleName === "SALESMAN" && user.Status === true,
            )
            .map((user: any) => ({
              value: user.LoginId,
              display: user.LoginId,
            }));
        },
        error: () => this.error("Unable to load sales consultants."),
      }),
    );
    this.subscriptions.add(
      this.stockHoldService.getActiveModelsInSales().subscribe({
        next: (response) => {
          if (!Array.isArray(response.body))
            return this.error("Unable to load vehicle models.");
          this.batchFormFields[0].options = response.body.map(
            (model: any) => ({
              value: model.Name,
              display: model.Name,
            }),
          );
        },
        error: () => this.error("Unable to load vehicle models."),
      }),
    );
  }

  initializeFormFields(): void {
    this.searchFormFields = [
      {
        type: "search",
        id: "vinNumber",
        label: "VIN Number",
        searchEvent: true,
        searchType: "vinSearch",
      },
      {
        type: "text",
        id: "engineNumber",
        label: "Engine Number",
        lock: true,
      },
      {
        type: "text",
        id: "chasisNumber",
        label: "Chassis Number",
        lock: true,
      },
      { type: "text", id: "model", label: "Model", lock: true },
      { type: "text", id: "colour", label: "Colour", lock: true },
      {
        type: "date",
        id: "dutyPaid",
        label: "Duty Paid",
        lock: true,
      },
      {
        type: "select",
        id: "stockStatus",
        label: "Stock Status",
        value: "H",
        options: [
          { value: "R", display: "Released (Visible to SE)" },
          { value: "H", display: "Hold (Invisible to SE)" },
          { value: "K", display: "Kept (Visible to SE)" },
        ],
      },
      {
        type: "date",
        id: "holdDate",
        label: "Hold Date (dd/mm/yyyy)",
      },
      {
        type: "select",
        id: "salesConsultant",
        label: "Sales Consultant",
        options: [],
      },
      { type: "text", id: "buyersName", label: "Buyer's Name" },
      {
        type: "text",
        id: "buyersNric",
        label: "Buyer's NRIC",
        lock: true,
      },
      {
        type: "text",
        id: "contractNumber",
        label: "Contract Number",
        lock: true,
      },
      {
        type: "text",
        id: "buyersOrderNumber",
        label: "Buyer's Order Number",
        lock: true,
      },
    ];
  }

  private value(fields: FormField[], id: string): string {
    return String(fields.find((f) => f.id === id)?.value ?? "");
  }

  onSingleChange(event: {
    fields: FormField[];
    updatedValue: number;
  }): void {
    this.searchFormFields = event.fields;
    if (event.fields[event.updatedValue]?.id === "vinNumber") {
      this.vehicleRequest?.unsubscribe();
      this.singleBusy = false;
      this.loadedVin = "";
      this.registered = false;
      this.resetSingleDetails();
    }
  }

  private resetSingleDetails(): void {
    this.searchFormFields = this.searchFormFields.map((field) => ({
      ...field,
      value:
        field.id === "vinNumber"
          ? field.value
          : field.id === "stockStatus"
            ? "H"
            : "",
    }));
  }

  searchByVIN(): void {
    if (this.singleBusy) return;
    const vin = this.value(this.searchFormFields, "vinNumber")
      .trim()
      .toUpperCase();
    if (!vin) return this.error("Please enter a VIN Number.");
    this.vehicleRequest?.unsubscribe();
    this.loadedVin = "";
    this.registered = false;
    this.resetSingleDetails();
    this.searchFormFields.find((f) => f.id === "vinNumber")!.value =
      vin;
    this.singleBusy = true;
    this.vehicleRequest = this.stockHoldService
      .getVehicleByCaseNumber(vin)
      .pipe(finalize(() => (this.singleBusy = false)))
      .subscribe({
        next: (response) => {
          if (
            (response.code !== 0 && response.code !== -2) ||
            !response.body?.CaseNumber
          ) {
            return this.error(
              response.message || "Unable to load vehicle details.",
            );
          }
          this.loadedVin = response.body.CaseNumber;
          this.registered = response.code === -2;
          this.populateFormWithData(response.body);
          if (this.registered) this.error(response.message);
        },
        error: () => this.error("Unable to load vehicle details."),
      });
  }

  populateFormWithData(data: any): void {
    const mapping: Record<string, string> = {
      vinNumber: "CaseNumber",
      engineNumber: "EngineNumber",
      chasisNumber: "ChasisNumber",
      model: "Model",
      colour: "Colour",
      dutyPaid: "DutyPaid",
      stockStatus: "StockStatus",
      holdDate: "HoldDate",
      salesConsultant: "SalesConsultant",
      buyersName: "BuyerName",
      buyersNric: "BuyerNric",
      contractNumber: "ContractNo",
      buyersOrderNumber: "RefNo",
    };
    this.searchFormFields = this.searchFormFields.map((field) => {
      let value = data[mapping[field.id]] ?? "";
      if (field.type === "date" && value) {
        const [day, month, year] = value.split("/");
        value = year + "-" + month + "-" + day;
      }
      return { ...field, value };
    });
  }

  updateEntry(): void {
    if (this.singleBusy) return;
    if (
      !this.loadedVin ||
      this.loadedVin !==
        this.value(this.searchFormFields, "vinNumber")
    ) {
      return this.error("Please search for the VIN before updating.");
    }
    if (this.registered)
      return this.error("This VIN Number is registered!");
    const get = (id: string) => this.value(this.searchFormFields, id);
    if (!["H", "R", "K"].includes(get("stockStatus")))
      return this.error("Please select a stock status.");
    // Form controls display null as an empty value; restore nullable API fields.
    const optional = (id: string): string | null => {
      const value = get(id);
      return value.trim() === "" ? null : value;
    };
    const payload = {
      model: {
        __type: "KAPS.Model.CrmStockHold",
        CaseNumber: this.loadedVin,
        EngineNumber: optional("engineNumber"),
        ChasisNumber: optional("chasisNumber"),
        Model: optional("model"),
        Colour: optional("colour"),
        DutyPaid: this.formatDateLiteral(get("dutyPaid")),
        StockStatus: get("stockStatus"),
        HoldDate: this.formatDateLiteral(get("holdDate")),
        SalesConsultant: optional("salesConsultant"),
        BuyerName: optional("buyersName"),
        BuyerNric: optional("buyersNric"),
        ContractNo: optional("contractNumber"),
        RefNo: optional("buyersOrderNumber"),
      },
    };
    this.singleBusy = true;
    this.subscriptions.add(
      this.stockHoldService.updateStockHold(payload).subscribe({
        next: (response) => {
          this.singleBusy = false;
          if (response.code !== 0)
            return this.error(
              response.message || "Unable to update stock hold.",
            );
          this.notifier.notify(
            "success",
            "Stock hold details updated successfully.",
          );
          this.singleBusy = false;
          this.searchByVIN();
        },
        error: () => {
          this.singleBusy = false;
          this.error("Unable to update stock hold.");
        },
      }),
    );
  }

  formatDateLiteral(value: string): string {
    // V1 represents absent dates as empty strings, not null.
    if (!value.trim()) return "";
    const [year, month, day] = value.split("-");
    return day + "/" + month + "/" + year;
  }

  clearEntry(): void {
    if (this.singleBusy) return;
    this.vehicleRequest?.unsubscribe();
    this.loadedVin = "";
    this.registered = false;
    this.searchFormFields.find((f) => f.id === "vinNumber")!.value =
      "";
    this.resetSingleDetails();
  }

  printEntry(): void {
    if (this.singleBusy) return;
    if (!this.loadedVin)
      return this.error("Please search for the VIN before printing.");
    const reportWindow = window.open("", "_blank");
    if (!reportWindow)
      return this.error("Please allow pop-ups to open the report.");
    reportWindow.opener = null;
    this.singleBusy = true;
    this.subscriptions.add(
      this.stockHoldService
        .getStockHoldReport(this.loadedVin)
        .pipe(finalize(() => (this.singleBusy = false)))
        .subscribe({
          next: (response) => {
            if (response.statusCode !== 200 || !response.body?.Name) {
              reportWindow.close();
              return this.error("No report available for this VIN.");
            }
            reportWindow.location.href = response.body.Name;
          },
          error: () => {
            reportWindow.close();
            this.error("Unable to generate the stock hold report.");
          },
        }),
    );
  }

  changeTab(tab: string): void {
    this.selectedTab = tab;
  }

  onBatchChange(event: {
    fields: FormField[];
    updatedValue: number;
  }): void {
    this.batchFormFields = event.fields;
    const changed = event.fields[event.updatedValue]?.id;
    if (changed === "model") {
      this.colourRequest?.unsubscribe();
      this.listRequest?.unsubscribe();
      this.vehicleList = [];
      this.batchMessage = "";
      const colour = this.batchFormFields.find(
        (f) => f.id === "colour",
      )!;
      colour.value = "";
      colour.options = [];
      const model = this.value(this.batchFormFields, "model");
      if (!model) return;
      this.colourRequest = this.stockHoldService
        .getColourDrpListByModel(model)
        .subscribe({
          next: (response) => {
            if (!Array.isArray(response.body))
              return this.error("Unable to load colours.");
            colour.options = response.body.map((item: any) => ({
              value: item.Value,
              display: item.Value + " - " + item.Name,
            }));
          },
          error: () => this.error("Unable to load colours."),
        });
    } else if (changed === "colour") {
      this.refreshVehicleList();
    }
  }

  refreshVehicleList(): void {
    this.listRequest?.unsubscribe();
    this.vehicleList = [];
    this.batchMessage = "";
    const model = this.value(this.batchFormFields, "model");
    const colour = this.value(this.batchFormFields, "colour");
    if (!model || !colour) return;
    this.batchMessage = "Loading vehicles...";
    this.listRequest = this.stockHoldService
      .getVehicleList(colour, model)
      .subscribe({
        next: (response) => {
          if (!Array.isArray(response.body)) {
            this.batchMessage = "Unable to load vehicles.";
            return this.error(this.batchMessage);
          }
          this.vehicleList = response.body.map((item: any) => ({
            vinNumber: item.CaseNumber,
            buyerName: item.BuyerName || "",
            etaDate: item.EtaDate,
            holdDate: item.HoldDate,
            stockStatus: item.StockStatus,
            selected: false,
          }));
          this.batchMessage = this.vehicleList.length
            ? ""
            : "No vehicles found for this model and colour.";
        },
        error: () => {
          this.batchMessage = "Unable to load vehicles.";
          this.error(this.batchMessage);
        },
      });
  }

  updateBatch(): void {
    if (this.batchBusy) return;
    const status = this.value(this.batchFormFields, "stockStatus");
    if (!["H", "R", "K"].includes(status))
      return this.error("Please select a stock status.");
    const selected = this.vehicleList.filter(
      (vehicle) => vehicle.selected,
    );
    if (!selected.length)
      return this.error("Please select at least one vehicle.");
    const payload = {
      modelList: selected.map((vehicle) => ({
        Name: vehicle.vinNumber,
        Value: status,
      })),
    };
    this.batchBusy = true;
    this.subscriptions.add(
      this.stockHoldService
        .updateAllStockHolds(payload)
        .pipe(finalize(() => (this.batchBusy = false)))
        .subscribe({
          next: (response) => {
            if (response.code !== 0)
              return this.error(
                response.message || "Unable to update stock holds.",
              );
            this.notifier.notify(
              "success",
              "Stock hold statuses updated successfully.",
            );
            this.refreshVehicleList();
          },
          error: () => this.error("Unable to update stock holds."),
        }),
    );
  }

  checkAll(): void {
    this.vehicleList.forEach((vehicle) => (vehicle.selected = true));
  }
  uncheckAll(): void {
    this.vehicleList.forEach((vehicle) => (vehicle.selected = false));
  }
  private error(message: string): void {
    this.notifier.notify("error", message);
  }
  ngOnDestroy(): void {
    this.vehicleRequest?.unsubscribe();
    this.colourRequest?.unsubscribe();
    this.listRequest?.unsubscribe();
    this.subscriptions.unsubscribe();
  }
}
